import { Component } from '@theme/component';

/**
 * @typedef {Object} LabiargeLookbookCoverRefs
 * @property {HTMLButtonElement} [scrollCue] - Optional scroll-cue button
 */

/**
 * Full-bleed editorial opening block. Handles the scroll-cue button click
 * so visitors can advance to the next section without manual scrolling.
 * Respects prefers-reduced-motion. When reduced, uses an instant jump
 * instead of smooth scroll to honour the user's motion contract.
 *
 * The `on:click="/scrollToNext"` attribute on the cue button (see
 * blocks/labiarge-lookbook-cover.liquid) delegates to the handler below
 * via Horizon's Component event system (component.js:210–298).
 *
 * @extends {Component<LabiargeLookbookCoverRefs>}
 */
class LabiargeLookbookCoverComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#respectReducedMotion();
  }

  /**
   * If the user prefers reduced motion, pause any hero background video so
   * they see only the poster frame. Reactive to OS-level preference changes
   * mid-session (user toggles the setting while the page is open).
   */
  #respectReducedMotion() {
    /** @type {HTMLVideoElement[]} */
    const videos = Array.from(this.querySelectorAll('video[data-reduced-motion-pause]'));
    if (videos.length === 0) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      for (const v of videos) {
        if (mq.matches) {
          v.pause();
          v.removeAttribute('autoplay');
        } else if (v.paused) {
          // Re-start autoplay when user un-prefers reduced motion.
          v.play().catch(() => {
            // Autoplay can be blocked (e.g. low-power mode); ignore.
          });
        }
      }
    };
    apply();
    mq.addEventListener('change', apply);
  }

  /**
   * Scroll the viewport to the top of the next sibling section/block
   * directly below this lookbook cover.
   *
   * @param {Event} event
   */
  scrollToNext(event) {
    event.preventDefault();

    // Prefer the next sibling at the block/section level. Walk up to the
    // nearest Shopify section wrapper so we find a sibling with actual
    // layout space. Bare inner divs don't make good scroll anchors.
    const target = this.#findNextAnchor();
    if (!target) return;

    const behavior = this.#prefersReducedMotion() ? 'auto' : 'smooth';
    target.scrollIntoView({ behavior, block: 'start' });

    // Move keyboard focus onto the landed content so the next tab stop is
    // in the newly-visible region, not still on the cue button.
    if (target instanceof HTMLElement) {
      // tabindex="-1" lets programmatic focus work without becoming a
      // tab stop on subsequent keyboard nav.
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  }

  /** @returns {Element | null} */
  #findNextAnchor() {
    // `this` is the <labiarge-lookbook-cover-component>. Walk up to the
    // nearest shopify-section wrapper (Shopify wraps every rendered section
    // in a .shopify-section div), then target its next sibling.
    /** @type {Element | null} */
    let node = this.closest('.shopify-section, section');
    if (!node) node = this;
    return node.nextElementSibling;
  }

  /** @returns {boolean} */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

customElements.define('labiarge-lookbook-cover-component', LabiargeLookbookCoverComponent);
